import * as React from 'react'
import { hashQueryKey } from '@tanstack/react-query'
import { ReadTransaction, Replicache } from 'replicache'
import { useReplicacheQueryV2 } from '~/hooks-data/use-replicache-query'
import {
  ExpectedSyncerError,
  syncers,
  type SyncerSubsMapItem,
  type Syncers,
} from '@basehub/data/replicache/syncers'
import { create } from 'zustand'
import { AsyncReturnType } from 'type-fest'
import { hash } from '@basehub/utils/hash'

export const useSyncerSubs = create<{
  syncerSubs: Map<
    string,
    { queryKeyHash: string; clientId: string; data: SyncerSubsMapItem }
  >
}>(() => {
  return {
    syncerSubs: new Map(),
  }
})

// Helper to extract the params type from a client function
type ExtractParamType<T> = T extends (
  tx: any,
  params: infer P,
  queryKeyHash: string
) => any
  ? P
  : never

// Get param types for a specific client getter function
type SubParams<K extends keyof Syncers> = ExtractParamType<Syncers[K]['client']>

/**
 * Custom syncer subscription hook
 * Note: Due to TypeScript limitations, autocomplete for params may show
 * parameters from all methods, but validation still works correctly.
 */
export function useCustomSyncerSub<K extends keyof Syncers>(
  replicache: Replicache | undefined,
  {
    syncer,
    params,
    extraDeps,
    keepPreviousData,
  }: {
    syncer: K
    params: SubParams<K>
    extraDeps?: string[]
    keepPreviousData?: boolean
  }
) {
  const syncerSubs = useSyncerSubs((s) => s.syncerSubs)
  const replName = replicache?.name

  const key = React.useMemo(
    () =>
      getQueryKey({
        replName,
        syncer,
        params,
        extraDeps,
      }),
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [replName, syncer, params, ...(extraDeps ?? [])]
  )
  const queryKeyHash = React.useMemo(() => hash(hashQueryKey(key)), [key])

  const paramsRef = React.useRef(params)
  // eslint-disable-next-line react-compiler/react-compiler
  paramsRef.current = params

  React.useEffect(() => {
    if (!replicache) return

    replicache.pull()

    syncerSubs.set(queryKeyHash, {
      queryKeyHash,
      clientId: replicache.clientID,
      data: { syncer, params: paramsRef.current },
    })

    return () => {
      syncerSubs.delete(queryKeyHash)
    }
  }, [replicache, syncer, queryKeyHash, syncerSubs])

  return useReplicacheQueryV2({
    key,
    replicache,
    fetcher: async (tx: ReadTransaction) => {
      try {
        return (await syncers[syncer].client(
          tx,
          params as any,
          queryKeyHash
        )) as AsyncReturnType<Syncers[K]['client']>
      } catch (e) {
        if (e instanceof ExpectedSyncerError) {
          throw e
        }
        console.error(`Error in syncer ${String(syncer)}:`, params)
        console.error(e)
        return null
      }
    },
    enabled: !!replicache,
    keepPreviousData,
  })
}

const getQueryKey = ({
  replName,
  syncer,
  extraDeps,
  params,
}: {
  replName: string | undefined
  syncer: string
  extraDeps?: string[]
  params: any
}) => {
  return [
    '__custom_syncer_sub_query',
    replName,
    syncer,
    params,
    ...(extraDeps ?? []),
  ]
}

export function addCustomSyncerSubSingle<K extends keyof Syncers>({
  replName,
  clientId,
  syncer,
  params,
}: {
  replName: string | undefined
  clientId: string
  syncer: K
  params: SubParams<K>
}) {
  const queryKeyHash = hash(
    hashQueryKey(getQueryKey({ replName, syncer, params }))
  )
  useSyncerSubs.setState((s) => {
    s.syncerSubs.set(queryKeyHash, {
      queryKeyHash,
      clientId,
      data: { syncer, params },
    })
    return s
  })
}
